import React, { JSX } from "react";

import { ExpandMore, Info } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { type SxProps } from "@mui/system";
import {
  AttributeWrapper,
  type AttributeWrapperProps as BaseAttributeWrapperProps,
  ConfigurationAttributeInfo,
  INFO_LAYOUT_PROPS_DEFAULTS,
  MarkdownOrText,
  useConfigurationAttributeScrolling,
} from "@plansoft-configuration-renderer/components";
import {
  useConfigurationAttributeName,
  useCsAttribute,
  useCsValueById,
  useLocalSelectValue,
} from "@plansoft-configuration-renderer/framework";

import { useConfigurationTreeContext } from "@/context/ConfigurationTreeContext";
import { useAccordionExpansion } from "@/hooks/useAccordionExpansion";
import { useSubstructureDisplayAttribute } from "@/hooks/useSubstructureDisplayAttribute";
import { useUrlParams } from "@/hooks/useUrlParams";

import { AccordionDisplayValue } from "./AccordionDisplayValue";
import { CustomAlert } from "./CustomAlert";

interface CustomAttributeWrapperProps extends BaseAttributeWrapperProps {
  identifier?: string;
  custom?: {
    slider?: boolean;
    visualStyle?: "default";
    renderAs?: "error" | "warning" | "info";
    alertIconName?: string;
    displayedValueAttributeId?: string;
    displayType?: string;
  };
}

const getDisplayedValueUnit = (
  unitSymbol?: string | null,
  unitName?: string | null,
  unitId?: string | null,
): string => {
  switch (unitSymbol) {
    case "pc":
      return unitName || "";
    default:
      return unitSymbol || unitId || "";
  }
};

export function CustomAttributeWrapper(
  props: CustomAttributeWrapperProps,
): JSX.Element {
  const { id, isAllowed, path, isComplete, isRequired, dataType } =
    useCsAttribute();
  const attribute = useCsAttribute();
  const { pinned, expanded: treeExpanded } = useConfigurationTreeContext();
  const { uiCulture } = useUrlParams();
  const elementRef = useConfigurationAttributeScrolling();
  const theme = useTheme();
  const selectedValueId = useLocalSelectValue();
  const selectedValue = useCsValueById(selectedValueId);
  const labelText = useConfigurationAttributeName();
  const isSubstructure = attribute.id.startsWith("S_");

  const substructureAttribute = useSubstructureDisplayAttribute(
    attribute.configurations,
    props.custom?.displayedValueAttributeId,
  );

  const { expanded, handleExpansion } = useAccordionExpansion({
    isComplete: attribute.isComplete,
    isSubstructure,
    selectedValueId,
    displayedValueAttribute: substructureAttribute,
  });

  const displayColumnName = attribute?.properties?.displaySelectedColumn?.value;
  const displayedValueUnit = getDisplayedValueUnit(
    attribute.unitSymbol,
    attribute.unitName,
    attribute.unitId,
  );

  const notAllowedProps: SxProps = isAllowed
    ? {}
    : {
        pointerEvents: "none",
        opacity: 0.75,
      };

  if (props.identifier === "S_RIGHT_VIEW") {
    return (
      <Box
        sx={{
          height: 720,
          width: pinned && treeExpanded ? 375 : 455,
          transition: "width 0.3s ease-in-out",
        }}
      >
        <AttributeWrapper {...props} />
      </Box>
    );
  }

  if (props.custom?.visualStyle === "default") {
    return <AttributeWrapper {...props} />;
  }

  if (props.custom?.renderAs) {
    return (
      <CustomAlert
        type={props.custom.renderAs}
        iconName={props.custom?.alertIconName}
      >
        <AttributeWrapper {...props} />
      </CustomAlert>
    );
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpansion}
      sx={{
        ...notAllowedProps,
        "& .MuiTextField-root": {
          "& label": { display: "none" },
          "& legend": { display: "none", width: "unset" },
          "& fieldset": { top: 0 },
        },
      }}
    >
      <AccordionSummary
        id={path}
        className="attribute-wrapper"
        expandIcon={<ExpandMore />}
        sx={{
          flexDirection: "row-reverse",
          padding: 0,
          gap: 2,
          borderColor:
            attribute.isRequired &&
            !(
              selectedValueId || selectedValueId === (0 as unknown as string)
            ) &&
            !expanded
              ? theme.palette.warning.main
              : undefined,
        }}
      >
        <Box display={"inline-flex"} sx={{ gap: 1, alignItems: "center" }}>
          <Typography>
            {labelText}
            {attribute.isRequired ? "*" : undefined}
          </Typography>
          {attribute.description && (
            <Tooltip
              title={
                <MarkdownOrText
                  // markdown has no <br>; map to hard line breaks
                  text={attribute.description.replace(
                    /<br\s*\/?>\s*/gi,
                    "  \n",
                  )}
                  textMode="markdown"
                />
              }
              placement={"right"}
              arrow
              slotProps={{
                tooltip: {
                  sx: { "& .MuiTypography-root": { fontSize: "inherit" } },
                },
              }}
            >
              <Info color={"primary"} />
            </Tooltip>
          )}
          <ConfigurationAttributeInfo
            {...INFO_LAYOUT_PROPS_DEFAULTS}
            infoButtonProps={{
              color: "primary",
              size: "medium",
              icon: <Info color="primary" />,
              sx: { p: 0 },
            }}
          />
        </Box>
        <AccordionDisplayValue
          selectedValue={selectedValue}
          displayColumnName={displayColumnName}
          tableId={attribute.tableId}
          isSubstructure={isSubstructure}
          displayedValueAttributeId={props.custom?.displayedValueAttributeId}
          dataType={dataType}
          unitSymbol={displayedValueUnit}
          culture={uiCulture}
          displayType={props.custom?.displayType}
          path={path}
        />
      </AccordionSummary>

      <AccordionDetails
        ref={elementRef}
        data-is-complete={isComplete}
        data-is-required={isRequired}
        data-attribute-path={path}
        data-attribute-id={id}
        sx={{
          transition: theme.transitions.create(["opacity"], {
            duration: theme.transitions.duration.standard,
          }),
          opacity: expanded ? 1 : 0,
        }}
      >
        {props.children}
      </AccordionDetails>
    </Accordion>
  );
}
